import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Sparkles, User, Home, LogIn, LogOut, Heart, Calendar as CalendarIcon, Menu, X, ChevronDown, Tags, Scissors, MessageCircle, Images, Settings } from 'lucide-react';
import { isAdminRole, isSuperAdminRole } from '../utils/helpers';

const Layout = ({ user, onLogout, onLoginClick, chatbotEnabled = true, onChatbotToggle, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = isAdminRole(user?.role);
  const isSuperAdmin = isSuperAdminRole(user?.role);
  const [apiVersion, setApiVersion] = useState('');

  // Obtener versión del backend automáticamente
  useEffect(() => {
    let alive = true;
    const fetchVersion = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/version`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (alive && data?.version) setApiVersion(String(data.version));
      } catch {
        // Silencioso: si el backend no responde, no mostramos versión
      }
    };
    fetchVersion();
    return () => { alive = false; };
  }, []);

  // Cliente
  const clientNavItems = [
    { path: '/', label: 'Servicios', icon: Sparkles },
    ...(user ? [{ path: '/mis-reservas', label: 'Mis Reservas', icon: User }] : [])
  ];

  // Admin (incluye Agenda dentro del menú; no hay botón extra)
  const adminNavItems = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    {
      path: '/admin/products',
      label: 'Productos',
      icon: Sparkles,
      submenuTitle: 'Mantenedores',
      children: [
        { path: '/admin/categories', label: 'Categorías', icon: Tags },
        { path: '/admin/carrusel', label: 'Carrusel', icon: Images }
      ]
    },
    ...(isSuperAdmin ? [
      { path: '/admin/manicuristas', label: 'Manicuristas', icon: Scissors }
    ] : []),
    { path: '/admin/bookings', label: 'Reservas', icon: User },
    { path: '/admin/agenda', label: 'Agenda', icon: CalendarIcon }
  ];

  const navItems = isAdmin ? adminNavItems : clientNavItems;

  const handleLogoutClick = () => {
    if (typeof onLogout === 'function') onLogout();
    navigate('/');
  };

  const handleChatbotToggle = () => {
    if (typeof onChatbotToggle === 'function') {
      onChatbotToggle(!chatbotEnabled);
    }
  };

  const socialLinks = [
    { href: 'https://www.instagram.com/mora.artt/', icon: <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg" alt="Instagram" className="w-5 h-5" /> },
    { href: 'mailto:emmanuel.villacura@inacapmail.cl', icon: <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gmail.svg" alt="Correo" className="w-5 h-5" /> },
    { href: 'https://wa.me/+569809393', icon: <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/whatsapp.svg" alt="WhatsApp" className="w-5 h-5" /> },
  ];

  // Estado menú móvil
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cerrar al cambiar de ruta
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Evitar scroll de fondo cuando menú está abierto
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileOpen]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Si se redimensiona a desktop, cerrar el menú móvil
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false); // md breakpoint
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50">
      {/* NAV */}
      <motion.nav
        className="bg-white/90 backdrop-blur-xl border-b border-pink-200/50 sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <motion.div
              className="flex items-center gap-3 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/')}
            >
              <img
                src="/images/001.gif"
                alt="Mora.Artt Logo"
                className="w-16 h-16 drop-shadow-lg rounded-full object-cover"
              />
              <h1 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                Mora.Artt
              </h1>
            </motion.div>
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2">
              <nav>
                <ul className="flex gap-6">
                  {navItems.map(item => (
                    <li key={item.path} className="relative group">
                      <div className="flex flex-col">
                        <Link
                          to={item.path}
                          aria-haspopup={item.children ? 'menu' : undefined}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 ${location.pathname === item.path || item.children?.some(child => child.path === location.pathname)
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-blue-50'}`}
                        >
                          {item.icon && <item.icon className="w-5 h-5" />}
                          {item.label}
                          {item.children && <ChevronDown className="w-4 h-4" />}
                        </Link>
                        {item.children && (
                          <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 absolute left-0 top-full pt-2 transition-all duration-200 z-50">
                            <div className="w-64 rounded-xl border border-pink-100 bg-white shadow-xl p-2">
                              <div className="px-3 py-2 text-xs font-bold text-purple-600 tracking-wide">
                                {item.submenuTitle}
                              </div>
                              {item.children.map(child => (
                                <Link
                                  key={child.path}
                                  to={child.path}
                                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === child.path
                                    ? 'bg-pink-100 text-pink-700'
                                    : 'text-gray-700 hover:bg-pink-50 hover:text-gray-900'}`}
                                >
                                  {child.icon && <child.icon className="w-4 h-4" />}
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </nav>
              {user ? (
                <div className="relative group">
                  <button
                    type="button"
                    aria-haspopup="menu"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:block max-w-[140px] truncate">{user.username}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 absolute right-0 top-full pt-2 transition-all duration-200 z-50">
                    <div className="w-64 rounded-xl border border-pink-100 bg-white shadow-xl p-2">
                      <div className="px-3 py-2 text-xs font-bold text-purple-600 tracking-wide">
                        Cuenta
                      </div>
                      {isSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => navigate('/admin/configuracion')}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-pink-50 hover:text-gray-900 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Configuracion</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleChatbotToggle}
                        role="switch"
                        aria-checked={chatbotEnabled}
                        aria-label={chatbotEnabled ? 'Desactivar chatbot' : 'Activar chatbot'}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-pink-50 hover:text-gray-900 transition-colors"
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <MessageCircle className="w-4 h-4" />
                          <span>Chatbot</span>
                        </span>
                        <span className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${chatbotEnabled ? 'bg-pink-500' : 'bg-gray-300'}`}>
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${chatbotEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handleLogoutClick}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Cerrar sesion</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <motion.button
                  onClick={onLoginClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300"
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                >
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:block">Ingresar</span>
                </motion.button>
              )}
            </div>

            {/* Botón hamburguesa móvil */}
            <button
              type="button"
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              className="hamburger-btn flex items-center justify-center rounded-lg p-2 text-pink-600"
              onClick={() => setMobileOpen(o => !o)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Panel móvil */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-nav-panel"
          >
            {/* Backdrop primero para quedar detrás del drawer (z-index menor) */}
            <div className="mobile-nav-backdrop" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: 'spring', stiffness: 240, damping: 26 }}
              className="mobile-nav-drawer relative z-10"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center gap-3 px-4 py-4 border-b border-pink-100">
                <img src="/images/001.gif" alt="Logo Mora.Artt" className="w-12 h-12 rounded-full object-cover" />
                <span className="font-semibold text-pink-600 text-base">Mora.Artt</span>
              </div>
              <nav className="p-2">
                <ul className="flex flex-col gap-1">
                  {navItems.map(item => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.path || item.children?.some(child => child.path === location.pathname)
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                          : 'text-gray-800 hover:bg-pink-50 active:bg-pink-100'}`}
                      >
                        {item.icon && <item.icon className="w-5 h-5" />}
                        <span>{item.label}</span>
                      </Link>
                      {item.children && (
                        <div className="ml-4 mt-1 mb-2 border-l border-pink-100 pl-3">
                          <div className="px-2 py-2 text-xs font-bold text-purple-600 tracking-wide">
                            {item.submenuTitle}
                          </div>
                          {item.children.map(child => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === child.path
                                ? 'bg-pink-100 text-pink-700'
                                : 'text-gray-800 hover:bg-pink-50 active:bg-pink-100'}`}
                            >
                              {child.icon && <child.icon className="w-4 h-4" />}
                              <span>{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                  {isSuperAdmin && (
                    <li>
                      <button
                        onClick={() => { navigate('/admin/configuracion'); setMobileOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/admin/configuracion' || location.pathname === '/usuarios'
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                          : 'text-gray-800 hover:bg-pink-50 active:bg-pink-100'}`}
                      >
                        <Settings className="w-5 h-5" /> <span>Configuracion</span>
                      </button>
                    </li>
                  )}
                  <li className="pt-2 border-t border-pink-100 mt-2">
                    {user ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold text-gray-800 bg-pink-50">
                          <User className="w-5 h-5" />
                          <span className="truncate">{user.username}</span>
                        </div>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => { navigate('/admin/configuracion'); setMobileOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-pink-50 active:bg-pink-100"
                          >
                            <Settings className="w-5 h-5" />
                            <span>Configuracion</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleChatbotToggle}
                          role="switch"
                          aria-checked={chatbotEnabled}
                          aria-label={chatbotEnabled ? 'Desactivar chatbot' : 'Activar chatbot'}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-pink-50 active:bg-pink-100"
                        >
                          <span className="flex items-center gap-3">
                            <MessageCircle className="w-5 h-5" />
                            <span>Chatbot</span>
                          </span>
                          <span className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${chatbotEnabled ? 'bg-pink-500' : 'bg-gray-300'}`}>
                            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${chatbotEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </span>
                        </button>
                        <button
                          onClick={() => { handleLogoutClick(); setMobileOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-red-50 active:bg-red-100"
                        >
                          <LogOut className="w-5 h-5" /> <span>Cerrar sesion</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { onLoginClick?.(); setMobileOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-green-50 active:bg-green-100"
                      >
                        <LogIn className="w-5 h-5" /> <span>Ingresar</span>
                      </button>
                    )}
                  </li>
                </ul>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MAIN */}
      <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">
        {children}
      </main>

      {/* FOOTER */}
      <footer className="w-full bg-gray-100 text-center py-4 border-t flex items-center justify-between px-6">
        <Heart className="text-pink-500 w-6 h-6" />
        <div className="flex items-center gap-4 justify-center flex-1">
          {socialLinks.map((link, idx) => (
            <a
              key={idx}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition duration-200 hover:scale-110 hover:[filter:_brightness(0)_saturate(100%)_invert(39%)_sepia(97%)_saturate(749%)_hue-rotate(314deg)_brightness(97%)_contrast(101%)]"
            >
              {link.icon}
            </a>
          ))}
          <span className="text-gray-500 text-sm ml-4">
            © {new Date().getFullYear()} Mora.Artt. Todos los derechos reservados. |©|  Emmanuel Villacura  Developer
          </span>
        </div>
        <div className="flex items-center gap-2">
          {apiVersion && (
            <span className="text-sm font-semibold text-gray-600 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
              v{apiVersion}
            </span>
          )}
          <Heart className="text-pink-500 w-6 h-6" />
        </div>
      </footer>
    </div>
  );
};

export default Layout;
